import angular from 'angular';
import { SharedAngular } from '@Client/@types/sharedAngular';

export const VIDEO_RECORDING_PHASE_CHANGED = 'VIDEO_RECORDING_PHASE_CHANGED';
export const VIDEO_RECORDING_COMPLETE = 'VIDEO_RECORDING_COMPLETE';

export enum RecordingPhase {
  Inactive = 'inactive',
  AcquiringMedia = 'acquiringMedia',
  Countdown = 'countdown',
  Recording = 'recording',
  Done = 'done'
}

export class VideoRecordingService {
  public static $inject = ['notificationService', 'pubsubService'];

  private displayStream: MediaStream | null = null;
  private micStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private currentPhase: RecordingPhase = RecordingPhase.Inactive;

  constructor(
    private notificationService: SharedAngular.NotificationService,
    private pubsubService: SharedAngular.PubSubService
  ) {}

  public getRecordingPhase(): RecordingPhase {
    return this.currentPhase;
  }

  public resetPhase(): void {
    this.setPhase(RecordingPhase.Inactive);
  }

  public async acquireMedia(): Promise<void> {
    this.setPhase(RecordingPhase.AcquiringMedia);

    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
    } catch {
      this.notificationService.showWarningToast(
        'Unable to access microphone. No audio will be recorded.'
      );
    }

    try {
      this.displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor',
          frameRate: 2,
          width: { max: 1920 },
          height: { max: 1080 }
        },
        audio: false
      });
      this.setPhase(RecordingPhase.Countdown);
    } catch {
      this.notificationService.showWarningToast(
        'Unable to access screen. Please allow screen access and try again.'
      );
      this.cleanupStreams();
      this.setPhase(RecordingPhase.Inactive);
    }
  }

  public startRecording(): void {
    if (!this.displayStream) {
      this.notificationService.showWarningToast(
        'Unable to access screen. Please allow screen access and try again.'
      );
      this.cleanupStreams();
      this.setPhase(RecordingPhase.Inactive);
      return;
    }

    this.setPhase(RecordingPhase.Recording);

    const combinedStream = new MediaStream([
      ...this.displayStream.getVideoTracks(),
      ...(this.micStream ? this.micStream.getAudioTracks() : [])
    ]);

    const defaultMimeType = 'video/webm;codecs=vp9,opus';
    const mimeType = MediaRecorder.isTypeSupported(defaultMimeType)
      ? defaultMimeType
      : 'video/webm';
    const recorder = new MediaRecorder(combinedStream, {
      mimeType: mimeType,
      videoBitsPerSecond: 5_000_000
    });
    this.mediaRecorder = recorder;
    this.recordedChunks = [];

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
      this.cleanupStreams();
      this.setPhase(RecordingPhase.Done);
      this.pubsubService.publish(VIDEO_RECORDING_COMPLETE, blob);
    };

    this.displayStream.getVideoTracks()[0].onended = () => {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
    };

    // Delay so any countdown timer UI has time to hide before recording starts
    setTimeout(() => recorder.start(), 400);
  }

  public stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  private setPhase(phase: RecordingPhase): void {
    this.currentPhase = phase;
    this.pubsubService.publish(VIDEO_RECORDING_PHASE_CHANGED, phase);
  }

  private cleanupStreams(): void {
    if (this.displayStream) {
      this.displayStream.getTracks().forEach((track) => track.stop());
      this.displayStream = null;
    }
    if (this.micStream) {
      this.micStream.getTracks().forEach((track) => track.stop());
      this.micStream = null;
    }
    this.mediaRecorder = null;
    this.recordedChunks = [];
  }
}

angular
  .module('flowingly.runner.services')
  .service('videoRecordingService', VideoRecordingService);

export type VideoRecordingServiceType = InstanceType<
  typeof VideoRecordingService
>;
